import { MutableRefObject, ReactNode, useCallback, useMemo } from 'react'

import type { F0FormSubmitResult, F0SectionConfig } from '@factorialco/f0-react'
import { f0FormField } from '@factorialco/f0-react'
import type {
  ProcurementValueObjectsPreferredPaymentMethodEnum,
  ProcurementValueObjectsPurchaseOrderStatusEnum,
  PurchaseOrderDetailFieldsFragment,
} from 'generated/resources/graphql'
import {
  PurchaseOrderDetailDocument,
  SavePurchaseOrderFieldsDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { sectionLabel } from 'modules/procurement/components/poTemplates/FieldEditor/translations'

import type { LineItemDraft } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/useEditPurchaseOrder'
import type { FieldWithValue } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/utils'
import {
  displayValue,
  groupHeaderFields,
  isEntityFieldType,
  resolveFieldLabel,
} from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/utils'

const VALID_PAYMENT_METHODS: readonly ProcurementValueObjectsPreferredPaymentMethodEnum[] = [
  'banktransfer',
  'cash',
  'virtualcard',
]

function toPaymentMethodEnum(
  value: unknown
): ProcurementValueObjectsPreferredPaymentMethodEnum | null {
  if (typeof value !== 'string' || value.length === 0 || value === PAYMENT_METHOD_NONE) return null
  const found = VALID_PAYMENT_METHODS.find((m) => m === value)
  return found ?? null
}

type HeaderSectionType =
  | 'general_information'
  | 'vendor_contact'
  | 'line_items'
  | 'notes_and_delivery'

const SECTION_ORDER: HeaderSectionType[] = [
  'general_information',
  'vendor_contact',
  'line_items',
  'notes_and_delivery',
]

// F0Form's internal `Object.entries(shape)` iteration assigns row position by
// key order. JavaScript orders integer-like string keys numerically before
// insertion-order keys, so using raw numeric DB IDs as shape keys breaks the
// template-defined field order. Prefix with a non-numeric token to force
// insertion-order traversal, and strip it before sending to the backend.
const FORM_KEY_PREFIX = 'f_'
export const toFormKey = (id: string): string => `${FORM_KEY_PREFIX}${id}`
const fromFormKey = (key: string): string =>
  key.startsWith(FORM_KEY_PREFIX) ? key.slice(FORM_KEY_PREFIX.length) : key

/**
 * Wraps a Zod schema with f0FormField metadata.
 * Follows the same pattern as dynamic_forms/adapters/f0/schemaAdapter.ts `withFieldMeta`.
 */
function withFieldMeta(
  schema: z.ZodTypeAny,
  label: string,
  section: string,
  config: Record<string, unknown>,
  row?: string
): z.ZodTypeAny {
  const nextConfig: { label: string } & Record<string, unknown> = {
    label,
    section,
    ...config,
  }

  if (row) {
    nextConfig.row = row
  }

  return f0FormField(schema, nextConfig)
}

function zodSchemaForFieldType(fieldType: string, required: boolean): z.ZodTypeAny {
  if (fieldType === 'date') {
    const dateSchema = z.date({
      required_error: i18n.t('validations.required'),
      invalid_type_error: i18n.t('validations.required'),
    })
    return required ? dateSchema : dateSchema.optional()
  }
  return required ? z.string().min(1) : z.string().optional()
}

function fieldTypeExtraConfig(
  fieldType: string,
  options: string[] | null | undefined
): Record<string, unknown> {
  switch (fieldType) {
    case 'long_text':
      return { fieldType: 'textarea' }
    case 'boolean':
      return {
        options: [
          {
            value: 'true',
            label: i18n.t('procurement.purchase_orders.detail.field_values.boolean_yes'),
          },
          {
            value: 'false',
            label: i18n.t('procurement.purchase_orders.detail.field_values.boolean_no'),
          },
        ],
      }
    case 'select':
      return {
        options: (options ?? []).map((opt) => ({ value: opt, label: opt })),
      }
    case 'number':
    case 'integer':
    case 'percentage':
      return { inputType: 'number' }
    case 'date':
      return { fieldType: 'date' }
    default:
      return {}
  }
}

type VendorRenderFn = (props: {
  value: string
  onChange: (value: string) => void
  label: string
  required: boolean
}) => ReactNode

export type EmployeeRenderFn = (props: {
  value: string | undefined
  onChange: (value: string | undefined) => void
  label: string
  required: boolean
  disabled: boolean
}) => ReactNode

export type CurrencyRenderFn = (props: {
  value: string
  onChange: (value: string) => void
  label: string
  required: boolean
}) => ReactNode

export type LegalEntityRenderFn = (props: {
  value: string
  onChange: (value: string) => void
  label: string
  required: boolean
}) => ReactNode

export type LineItemsRenderFn = () => ReactNode

/**
 * Entity field types that have a custom renderer and send IDs/enum values
 * in the mutation (not display strings). Derived from the fieldType directly.
 */
function hasEntityRenderer(fieldType: string): boolean {
  switch (fieldType) {
    case 'vendor':
    case 'employee':
    case 'team':
    case 'payment_method':
    case 'currency':
    case 'legal_entity':
    case 'cost_center':
    case 'project':
      return true
    default:
      return false
  }
}

/**
 * Normalizes payment method values from Ruby T::Enum format (bank_transfer)
 * to GraphQL enum format (banktransfer) so form options match.
 */
function normalizePaymentMethodValue(value: string): string {
  switch (value) {
    case 'bank_transfer':
      return 'banktransfer'
    case 'virtual_card':
      return 'virtualcard'
    default:
      return value
  }
}

const PAYMENT_METHOD_NONE = '__none__'

function paymentMethodOptions(): Array<{ value: string; label: string }> {
  return [
    {
      value: PAYMENT_METHOD_NONE,
      label: i18n.t('procurement.purchase_orders.detail.payment.payment_types.unassigned'),
    },
    {
      value: 'banktransfer',
      label: i18n.t('procurement.purchase_orders.detail.payment.payment_types.bank_transfer'),
    },
    {
      value: 'cash',
      label: i18n.t('procurement.purchase_orders.detail.payment.payment_types.manual_payment'),
    },
    {
      value: 'virtualcard',
      label: i18n.t('procurement.purchase_orders.detail.payment.payment_types.single_use_card'),
    },
  ]
}

interface SchemaResult {
  schema: z.ZodObject<z.ZodRawShape>
  sections: Record<string, F0SectionConfig>
}

interface EntityRenders {
  vendorRender: VendorRenderFn | null
  employeeRender: EmployeeRenderFn | null
  teamSource: unknown
  legalEntityRender: LegalEntityRenderFn | null
  legalEntitySource: unknown
  currencyRender: CurrencyRenderFn | null
  lineItemsRender: LineItemsRenderFn | null
  costCenterSource: unknown
  projectSource: unknown
}

function buildEntityFieldConfig(
  def: FieldWithValue['definition'],
  label: string,
  isRequired: boolean,
  renders: EntityRenders
): Record<string, unknown> | null {
  const { vendorRender, employeeRender } = renders

  switch (def.fieldType) {
    case 'vendor':
      if (!vendorRender) return null
      return {
        fieldType: 'custom',
        render: ({ value, onChange }: { value: string; onChange: (v: string) => void }) =>
          vendorRender({ value, onChange, label, required: isRequired }),
      }
    case 'employee':
      if (!employeeRender) return null
      return {
        fieldType: 'custom',
        render: ({
          value,
          onChange,
          disabled,
        }: {
          value: string
          onChange: (v: string) => void
          disabled: boolean
        }) =>
          employeeRender({
            value: value || undefined,
            onChange: (v) => onChange(v ?? ''),
            label,
            required: isRequired,
            disabled,
          }),
      }
    case 'team':
      if (!renders.teamSource) return null
      return {
        fieldType: 'select',
        source: renders.teamSource,
        mapOptions: (t: { id: string; name: string }) => ({
          value: t.id,
          label: t.name,
          item: t,
        }),
        showSearchBox: true,
      }
    case 'payment_method':
      return {
        options: paymentMethodOptions(),
      }
    case 'currency':
      if (!renders.currencyRender) return null
      return {
        fieldType: 'custom',
        render: ({ value, onChange }: { value: string; onChange: (v: string) => void }) =>
          renders.currencyRender?.({ value, onChange, label, required: isRequired }),
      }
    case 'legal_entity':
      if (renders.legalEntityRender) {
        const legalEntityRenderFn = renders.legalEntityRender
        return {
          fieldType: 'custom',
          render: ({ value, onChange }: { value: string; onChange: (v: string) => void }) =>
            legalEntityRenderFn({ value, onChange, label, required: isRequired }),
        }
      }
      if (!renders.legalEntitySource) return null
      return {
        fieldType: 'select',
        source: renders.legalEntitySource,
        mapOptions: (le: { id: string; legalName: string }) => ({
          value: le.id,
          label: le.legalName,
          item: le,
        }),
        showSearchBox: true,
      }
    case 'cost_center':
      if (!renders.costCenterSource) return null
      return {
        fieldType: 'select',
        source: renders.costCenterSource,
        mapOptions: (cc: { id: string; name: string; code: string | null }) => ({
          value: cc.id,
          label: cc.code ? `${cc.name} (${cc.code})` : cc.name,
          item: cc,
        }),
        showSearchBox: true,
        clearable: true,
      }
    case 'project':
      if (!renders.projectSource) return null
      return {
        fieldType: 'select',
        source: renders.projectSource,
        mapOptions: (p: { id: string; name: string; code: string | null }) => ({
          value: p.id,
          label: p.code ? `${p.name} (${p.code})` : p.name,
          item: p,
        }),
        showSearchBox: true,
        clearable: true,
      }
    default:
      return null
  }
}

function buildEditSchema(
  fieldsBySection: Map<HeaderSectionType, FieldWithValue[]>,
  renders: EntityRenders
): SchemaResult {
  const shape: z.ZodRawShape = {}
  const sections: Record<string, F0SectionConfig> = {}

  for (const sectionKey of SECTION_ORDER) {
    // Line items section: rendered via a custom field placeholder
    if (sectionKey === 'line_items') {
      if (renders.lineItemsRender) {
        const lineItemsRenderFn = renders.lineItemsRender
        sections['line_items'] = { title: '' }
        shape['__line_items_placeholder__'] = withFieldMeta(
          z.string().optional(),
          '',
          'line_items',
          {
            fieldType: 'custom',
            render: () => lineItemsRenderFn(),
          }
        )
      }
      continue
    }

    const fields = fieldsBySection.get(sectionKey) ?? []
    if (fields.length === 0) continue

    sections[sectionKey] = { title: sectionLabel(sectionKey) }

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i]
      if (!field) continue
      const def = field.definition
      const label = resolveFieldLabel(def)
      const fieldId = def.id
      // long_text fields span full width (no row pairing)
      const isFullWidth = def.fieldType === 'long_text'
      const rowId = isFullWidth ? undefined : `${sectionKey}-row-${Math.floor(i / 2)}`

      // Required follows the per-template config (`def.required`).
      // Computed fields are auto-filled (read-only) so they cannot be required by the user.
      const isEntity = isEntityFieldType(def.fieldType)
      const isRequired = def.required && !def.computed
      const entityConfig = isEntity ? buildEntityFieldConfig(def, label, isRequired, renders) : null

      // Computed fields are disabled.
      // Entity fields without a custom renderer are also disabled (can't edit IDs as text).
      const isDisabled = def.computed || (isEntity && !entityConfig)

      if (entityConfig) {
        const zodSchema = zodSchemaForFieldType('text', isRequired)
        shape[toFormKey(fieldId)] = withFieldMeta(
          zodSchema,
          label,
          sectionKey,
          {
            disabled: isDisabled,
            ...entityConfig,
          },
          rowId
        )
      } else {
        const zodSchema = zodSchemaForFieldType(def.fieldType, isRequired)
        const extraConfig = fieldTypeExtraConfig(def.fieldType, def.options)
        shape[toFormKey(fieldId)] = withFieldMeta(
          zodSchema,
          label,
          sectionKey,
          {
            disabled: isDisabled,
            ...extraConfig,
          },
          rowId
        )
      }
    }
  }

  return { schema: z.object(shape), sections }
}

interface UsePurchaseOrderEditFormResult {
  schema: z.ZodObject<z.ZodRawShape>
  sections: Record<string, F0SectionConfig>
  defaultValues: Record<string, string | Date>
  onSubmit: (data: Record<string, unknown>) => Promise<F0FormSubmitResult>
}

export function usePurchaseOrderEditForm(
  purchaseOrder: PurchaseOrderDetailFieldsFragment,
  companyId: string,
  lineItemsRef: MutableRefObject<LineItemDraft[]>,
  onSuccess: () => void,
  renders: EntityRenders,
  targetStatus?: ProcurementValueObjectsPurchaseOrderStatusEnum
): UsePurchaseOrderEditFormResult {
  const [saveMutation] = useMutation(SavePurchaseOrderFieldsDocument, {
    extractorFn: (data) => data.procurementMutations.updateProcurementPurchaseOrder,
  })

  const fieldsBySection = useMemo(() => {
    const definitions = purchaseOrder.poTemplateVersion?.poTemplateFieldDefinitions ?? []
    const values = purchaseOrder.poFieldValues
    return groupHeaderFields(definitions, values)
  }, [purchaseOrder.poTemplateVersion, purchaseOrder.poFieldValues])

  const { schema, sections } = useMemo(
    () => buildEditSchema(fieldsBySection, renders),
    [fieldsBySection, renders]
  )

  const defaultValues = useMemo(() => {
    const values: Record<string, string | Date> = {}
    for (const fields of fieldsBySection.values()) {
      for (const field of fields) {
        const fieldId = field.definition.id
        const formKey = toFormKey(fieldId)
        const ft = field.definition.fieldType

        if (ft === 'vendor') {
          // Vendor default is the vendor ID
          values[formKey] = purchaseOrder.vendor?.id ?? ''
        } else if (ft === 'currency') {
          // Currency EAV stores ISO code; fall back to PO cost currency
          values[formKey] = field.value || purchaseOrder.cost.currency
        } else if (ft === 'legal_entity') {
          // Legal entity EAV stores ID; fall back to PO legal entity
          values[formKey] = field.value || purchaseOrder.legalEntity.id
        } else if (hasEntityRenderer(ft)) {
          // Entity fields with renderers (employee, team, payment_method)
          // use the raw EAV value (which stores the ID or enum string)
          let entityDefault = field.value ?? ''
          if (ft === 'payment_method') {
            if (!entityDefault) {
              entityDefault = purchaseOrder.preferredPaymentMethod ?? ''
            }
            entityDefault = normalizePaymentMethodValue(entityDefault)
            // Use sentinel for empty payment method so F0 Select can render it
            if (!entityDefault) {
              entityDefault = PAYMENT_METHOD_NONE
            }
          }
          values[formKey] = entityDefault
        } else if (isEntityFieldType(ft)) {
          // Entity fields without renderers show display values (disabled)
          values[formKey] = displayValue(field, purchaseOrder)
        } else if (field.definition.fieldType === 'date') {
          // F0Form date fields expect Date objects, not strings
          if (field.value) {
            const parsed = stringToDate(field.value, 'backend')
            if (parsed.isValid()) {
              values[formKey] = parsed.toDate()
            }
          }
        } else {
          values[formKey] = field.value ?? ''
        }
      }
    }
    return values
  }, [fieldsBySection, purchaseOrder])

  const onSubmit = useCallback(
    async (data: Record<string, unknown>): Promise<F0FormSubmitResult> => {
      const headerFieldValuesInput = Object.entries(data)
        .filter(([fieldKey]) => {
          // Skip the line items placeholder — it's a dummy field for visual rendering
          if (fieldKey === '__line_items_placeholder__') return false
          const fieldId = fromFormKey(fieldKey)
          for (const fields of fieldsBySection.values()) {
            const field = fields.find((f) => f.definition.id === fieldId)
            if (field) {
              const def = field.definition
              if (def.computed) return false
              // Entity fields with renderers send IDs/enum values — include them
              if (hasEntityRenderer(def.fieldType)) return true
              // Other entity fields are disabled display values — exclude them
              if (isEntityFieldType(def.fieldType)) return false
              return true
            }
          }
          return true
        })
        .map(([fieldKey, rawValue]) => {
          let value: string | null = null
          if (rawValue instanceof Date) {
            value = i18nDateToString(rawValue, 'backend')
          } else if (typeof rawValue === 'string' && rawValue.length > 0) {
            // Convert sentinel back to null for storage
            value = rawValue === PAYMENT_METHOD_NONE ? null : rawValue
          }
          return { fieldDefinitionId: fromFormKey(fieldKey), value }
        })

      const lineItems = lineItemsRef.current
      const lineItemsInput = lineItems.map((li) => ({
        id: li.id ?? null,
        position: li.position,
        fieldValues: li.fieldValues.map((fv) => ({
          fieldDefinitionId: fv.fieldDefinitionId,
          value: fv.value || null,
        })),
      }))

      // Resolve vendor ID from form data or existing PO
      const vendorFieldDef = [...fieldsBySection.values()]
        .flat()
        .find((f) => f.definition.fieldKey === 'vendor')
      const vendorRawValue = vendorFieldDef ? data[toFormKey(vendorFieldDef.definition.id)] : null
      const vendorId =
        (typeof vendorRawValue === 'string' && vendorRawValue.length > 0 ? vendorRawValue : null) ??
        purchaseOrder.vendor?.id ??
        null

      // Resolve currency from form data or existing PO
      const currencyFieldDef = [...fieldsBySection.values()]
        .flat()
        .find((f) => f.definition.fieldKey === 'currency')
      const currencyRawValue = currencyFieldDef
        ? data[toFormKey(currencyFieldDef.definition.id)]
        : null
      const currency =
        typeof currencyRawValue === 'string' && currencyRawValue.length > 0
          ? currencyRawValue
          : purchaseOrder.cost.currency

      // Resolve legal entity ID from form data or existing PO
      const legalEntityFieldDef = [...fieldsBySection.values()]
        .flat()
        .find((f) => f.definition.fieldKey === 'legal_entity')
      const legalEntityRawValue = legalEntityFieldDef
        ? data[toFormKey(legalEntityFieldDef.definition.id)]
        : null
      const legalEntityId =
        typeof legalEntityRawValue === 'string' && legalEntityRawValue.length > 0
          ? legalEntityRawValue
          : purchaseOrder.legalEntity.id

      // Resolve payment method from form data or existing PO
      const paymentMethodFieldDef = [...fieldsBySection.values()]
        .flat()
        .find((f) => f.definition.fieldKey === 'payment_method')
      const paymentMethodRawValue = paymentMethodFieldDef
        ? data[toFormKey(paymentMethodFieldDef.definition.id)]
        : null
      const preferredPaymentMethod = toPaymentMethodEnum(paymentMethodRawValue)

      const result = await saveMutation({
        variables: {
          id: purchaseOrder.id,
          companyId,
          status: targetStatus ?? purchaseOrder.status,
          cost: {
            cents: purchaseOrder.cost.cents,
            currency,
          },
          description: purchaseOrder.description,
          formattedPoNumber: purchaseOrder.formattedPoNumber,
          legalEntityId,
          vendorId,
          preferredPaymentMethod,
          headerFieldValues: headerFieldValuesInput,
          lineItems: lineItemsInput,
        },
        refetchQueries: [PurchaseOrderDetailDocument],
      })

      const handled = handleResult(result, (errors) => {
        const messages = Object.values(errors).flat()
        app.setFlash({
          type: 'wrong',
          message: messages[0] ?? i18n.t('procurement.purchase_orders.detail.save_error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('procurement.purchase_orders.detail.save_success'),
        })
        onSuccess()
        return { success: true }
      }

      return { success: false }
    },
    [fieldsBySection, purchaseOrder, companyId, saveMutation, lineItemsRef, onSuccess, targetStatus]
  )

  return useMemo(
    () => ({
      schema,
      sections,
      defaultValues,
      onSubmit,
    }),
    [schema, sections, defaultValues, onSubmit]
  )
}
